import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"
import LicenseFeatureGroupViewInfo from "@/v2-backoffice-client/license/featureGroup/view/LicenseFeatureGroupViewInfo"
import LicenseFeatureGroupViewToolbar from "@/v2-backoffice-client/license/featureGroup/view/LicenseFeatureGroupViewToolbar"
import LicenseFeatureGroupFeaturesTab from "@/v2-backoffice-client/license/featureGroup/view/LicenseFeatureGroupFeaturesTab"
import LicenseFeatureGroupIncludedInTab from "@/v2-backoffice-client/license/featureGroup/view/LicenseFeatureGroupIncludedInTab"

// The package panel (?v=featureGroup:<id>). Fed by the same
// getFeatureGroups list the board renders, so board→panel opens from the
// Apollo cache; the tab badges read the row's own counts (same rules as the
// tab reads). Features is the package's contents, editable in place;
// "Included in" is the usage picture, licenses and add-ons as two sections of
// one tab — everything a delete needs detached first, deep-linkable panel
// to panel.
function LicenseFeatureGroupViewSheet() {
  const { id } = useAppViewSheetOpener()
  const featureGroupId = Number(id)

  const { data, previousData, loading, error } = useFeatureGroupsQuery({
    skip: !featureGroupId,
  })
  // previousData keeps the panel rendered through post-save evictions,
  // when the cache is briefly empty and data undefined.
  const featureGroups = (data ?? previousData)?.getFeatureGroups ?? []
  const featureGroup = featureGroups.find(
    (candidate) => candidate.Id === featureGroupId,
  )

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading && !featureGroup}>
          {featureGroup?.Name}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <LicenseFeatureGroupViewInfo
          featureGroup={featureGroup}
          isLoading={loading && !featureGroup}
        />
        <LicenseFeatureGroupViewToolbar featureGroup={featureGroup} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="license-feature-group-view-tabs"
        defaultValue="features"
        // Only gate the tabs while there is nothing to show — see the license
        // sheet: gating on every cache-and-network revalidation would
        // unmount and flash the whole panel.
        isLoading={loading && !featureGroup}
        error={error}
        isEmpty={!loading && !featureGroup}
        emptyLabel="Package not found."
      >
        <Tabs.List>
          <Tabs.Trigger value="features" count={featureGroup?.FeatureCount}>
            Features
          </Tabs.Trigger>
          <Tabs.Trigger
            value="includedIn"
            count={
              featureGroup
                ? featureGroup.PlanCount + featureGroup.AddOnCount
                : undefined
            }
          >
            Included in
          </Tabs.Trigger>
        </Tabs.List>

        {/* flex-1 and no overflow-auto: the browser editor fills the whole
            tab height and scrolls its tree and list panes independently. */}
        <Tabs.Content value="features" className="min-h-0 flex-1">
          {featureGroup && (
            <LicenseFeatureGroupFeaturesTab featureGroup={featureGroup} />
          )}
        </Tabs.Content>

        <Tabs.Content value="includedIn" className="min-h-0 overflow-auto">
          {featureGroup && (
            <LicenseFeatureGroupIncludedInTab
              featureGroupId={featureGroup.Id}
            />
          )}
        </Tabs.Content>
      </Tabs>
    </>
  )
}

export default LicenseFeatureGroupViewSheet
